<template>
  <div class="gam-rating-unknown">
    <div class="unknown-options" role="radiogroup" aria-label="Estimated chess level">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="unknown-option"
        :class="{ active: selectedRating === option.value }"
        role="radio"
        :aria-checked="selectedRating === option.value"
        @click="selectRating(option.value)"
      >
        <span class="unknown-option__label">{{ option.label }}</span>
        <span class="unknown-option__dots" aria-hidden="true">
          <span v-for="d in option.stars" :key="d" :class="['dot', { filled: selectedRating === option.value }]" />
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RatingDisplayMode } from '@/core/data/user/user.type';
import { useRatingStore } from '@/stores/rating.store';
import { computed } from 'vue';

const ratingStore = useRatingStore();

const options = [
  { label: '< 10 games played', stars: 1, value: 150 },
  { label: '> 10 games played', stars: 2, value: 800 },
  { label: '> 100 games played', stars: 3, value: 1500 },
] as const;

const selectedRating = computed(() => {
  const currentRating = Number(ratingStore.ratings.rating);
  return options.some((option) => option.value === currentRating) ? currentRating : undefined;
});

const selectRating = (value: number) => {
  ratingStore.setOfflineRating(value);
  ratingStore.setRatingDisplayMode(RatingDisplayMode.DOTS);
};
</script>

<style lang="scss">
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-rating-unknown {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding-top: var(--spacing-md);

  .message-wrapper {
    font-size: 14px;
    line-height: 120%;
    font-weight: 400;
    color: var(--color-white-70);
    padding: var(--spacing-md) 0;
    text-wrap: balance;
    // text-align: center;
    margin-left: 42px;
    margin-top: -10px;
    margin-bottom: 10px;
  }

  .unknown-options {
    display: grid;
    gap: var(--spacing-sm);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .unknown-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-sm);
    width: 100%;
    border: 1px solid var(--color-white-10);
    border-radius: 10px;
    background: transparent;
    color: var(--color-white);
    padding: 12px 14px;
    cursor: pointer;
    transition:
      border-color 120ms ease,
      background-color 120ms ease;

    @include hover {
      border-color: var(--color-white-30);
      background: rgb(255 255 255 / 4%);
    }

    &.active {
      border-color: var(--color-primary, var(--color-white));
      background: rgb(255 255 255 / 8%);
    }
  }

  .unknown-option__label {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
  }

  .unknown-option__dots {
    display: flex;
    gap: 5px;
    align-items: center;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 1px;
    border: 1.5px solid var(--color-white-50);
    background: transparent;
    transition:
      background-color 120ms ease,
      border-color 120ms ease;

    &.filled {
      background: var(--color-white);
      border-color: var(--color-white);
    }
  }
}
</style>
